import {Helmet} from '@dr.pogodin/react-helmet';
import {openConfirmDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';
import {isString} from 'ramda-adjunct';

import {
  useDeleteArticleCategoryMutation,
  usePatchArticleCategoryMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  logError,
  usePermissions,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['name', 'isDefault', 'isActive', 'isEditable'] as const;

export function WarehouseArticleCategory() {
  const navigate = useNavigate();

  const [patchArticleCategory] = usePatchArticleCategoryMutation();
  const [deleteArticleCategory] = useDeleteArticleCategoryMutation();

  const [warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryCreate'],
  });

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.warehouse.labels.category'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.warehouse.actions.newCategory'),
        onClick: () => {
          navigate(settingsRoutes.warehousesArticleCategoryCreate);
        },
        isDisabled: !warehouseDictionaryCreate,
        tooltip: !warehouseDictionaryCreate
          ? i18n.t('module.warehouse.label.missingWarehouseDictionaryCreatePermission')
          : undefined,
        'data-testid': testIds.settings.warehousesArticleCategory('newArticleCategory'),
      },
    ],
  };

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = async ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    if (!isString(rowId)) {
      return;
    }

    const name = getColumnData<string>('name')?.value;
    const isDefault = getColumnData<boolean>('isDefault')?.value;
    const isActive = getColumnData<boolean>('isActive')?.value;
    const isEditable = getColumnData<boolean>('isEditable')?.value;

    if (isNil(name)) {
      return logError('name is not defined');
    }

    if (isNil(isDefault)) {
      return logError('isDefault is not defined');
    }

    if (isNil(isActive)) {
      return logError('isActive is not defined');
    }

    if (isNil(isEditable)) {
      return logError('isEditable is not defined');
    }

    // if row is editable then system = false else system = true
    const system = !isEditable;

    await match(actionKey)
      .with('redirectDetail', 'edit', () =>
        navigate(
          composePath(settingsRoutes.warehousesArticleCategoryDetail, {
            params: {id: rowId},
          })
        )
      )
      .with('delete', () =>
        openConfirmDialog({
          text: i18n.t('general.actions.delete'),
          onConfirm: () => {
            deleteArticleCategory({articleCategoryId: rowId})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() => refreshData())
              .catch(handleApiError);
          },
        })
      )
      .with('setAsDefault', () =>
        patchArticleCategory({
          articleCategoryId: rowId,
          body: {name, isDefault: true, active: isActive, system},
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('setAsActive', () =>
        patchArticleCategory({
          articleCategoryId: rowId,
          body: {name, isDefault, active: true, system},
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('setAsInactive', () =>
        patchArticleCategory({
          articleCategoryId: rowId,
          body: {name, isDefault, active: false, system},
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.articleCategories')} />
      <SettingsTemplate
        data-testid={testIds.settings.warehousesArticleCategory('template')}
        header={header}
      >
        <DmsDataGrid
          gridCode="warehouse-article-category"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
        />
      </SettingsTemplate>
    </>
  );
}
